import { type DragEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn, Button } from "@cieda/djayma-ui";
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  FolderClosed,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import type { Category } from "@/lib/admin-api/categories";
import type { ReorderItem } from "@/lib/admin-api/categories";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_DEPTH = 3;
const INDENT_PX = 24;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CategoryTreeProps {
  readonly categories: readonly Category[];
  readonly isLoading?: boolean;
  readonly onEdit: (category: Category) => void;
  readonly onAddChild: (parent: Category) => void;
  readonly onDelete: (category: Category) => void;
  readonly onReorder: (items: readonly ReorderItem[]) => void;
}

// ---------------------------------------------------------------------------
// Status badge (inline, reused from StatusBadge pattern)
// ---------------------------------------------------------------------------

function ActiveBadge({ isActive }: { readonly isActive: boolean }) {
  const { t } = useTranslation("admin");
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        isActive
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-stone-200 bg-stone-50 text-stone-500",
      )}
    >
      {isActive ? t("status.active") : t("status.inactive")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Single tree row
// ---------------------------------------------------------------------------

interface TreeRowProps {
  readonly category: Category;
  readonly depth: number;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly onEdit: (category: Category) => void;
  readonly onAddChild: (parent: Category) => void;
  readonly onDelete: (category: Category) => void;
  readonly onDragStart: (e: DragEvent, category: Category) => void;
  readonly onDragOver: (e: DragEvent, category: Category) => void;
  readonly onDrop: (e: DragEvent, category: Category) => void;
  readonly isDragTarget: boolean;
}

function TreeRow({
  category,
  depth,
  isExpanded,
  onToggle,
  onEdit,
  onAddChild,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  isDragTarget,
}: TreeRowProps) {
  const { t } = useTranslation("admin");
  const hasChildren = category.children.length > 0;
  const canAddChild = depth < MAX_DEPTH - 1;

  return (
    <div
      className={cn(
        "group flex items-center gap-2 border-b border-stone-100 px-3 py-2.5 transition-colors",
        "hover:bg-stone-50",
        isDragTarget && "bg-amber-50 ring-1 ring-inset ring-amber-300",
      )}
      style={{ paddingLeft: `${depth * INDENT_PX + 12}px` }}
      draggable
      onDragStart={(e) => onDragStart(e, category)}
      onDragOver={(e) => onDragOver(e, category)}
      onDrop={(e) => onDrop(e, category)}
    >
      {/* Drag handle */}
      <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-stone-300 opacity-0 group-hover:opacity-100" />

      {/* Expand/collapse toggle */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded text-stone-400 hover:text-stone-600",
          !hasChildren && "invisible",
        )}
        aria-label={isExpanded ? t("categories.collapse") : t("categories.expand")}
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Folder icon */}
      {isExpanded && hasChildren ? (
        <FolderOpen className="h-4 w-4 shrink-0 text-amber-500" />
      ) : (
        <FolderClosed className="h-4 w-4 shrink-0 text-stone-400" />
      )}

      {/* Name */}
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-stone-800">
        {category.name_fr}
      </span>

      {/* Product count */}
      <span className="hidden shrink-0 text-xs text-stone-400 sm:inline">
        {t("categories.product_count", { count: category.product_count })}
      </span>

      {/* Sort order */}
      <span className="hidden shrink-0 text-xs tabular-nums text-stone-300 lg:inline">
        #{category.sort_order}
      </span>

      {/* Status badge */}
      <ActiveBadge isActive={category.is_active} />

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          onClick={() => onEdit(category)}
          className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
          aria-label={t("actions.edit")}
          title={t("actions.edit")}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>

        {canAddChild && (
          <button
            type="button"
            onClick={() => onAddChild(category)}
            className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-emerald-600"
            aria-label={t("categories.add_child")}
            title={t("categories.add_child")}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}

        <button
          type="button"
          onClick={() => onDelete(category)}
          className="rounded p-1 text-stone-400 hover:bg-red-50 hover:text-red-600"
          aria-label={t("actions.delete")}
          title={t("actions.delete")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TreeSkeleton() {
  return (
    <div className="animate-pulse space-y-2 p-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3"
          style={{ paddingLeft: `${(i % 3) * INDENT_PX}px` }}
        >
          <div className="h-4 w-4 rounded bg-stone-200" />
          <div className="h-4 flex-1 rounded bg-stone-200" style={{ maxWidth: `${180 - i * 20}px` }} />
          <div className="h-5 w-12 rounded-full bg-stone-200" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile flat list view
// ---------------------------------------------------------------------------

function MobileRow({
  category,
  depth,
  onEdit,
  onDelete,
}: {
  readonly category: Category;
  readonly depth: number;
  readonly onEdit: (c: Category) => void;
  readonly onDelete: (c: Category) => void;
}) {
  const { t } = useTranslation("admin");

  return (
    <div
      className="flex items-center justify-between border-b border-stone-100 px-3 py-3"
      style={{ paddingLeft: `${depth * 16 + 12}px` }}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-stone-800">
          {category.name_fr}
        </p>
        <p className="text-xs text-stone-400">
          {t("categories.product_count", { count: category.product_count })}
        </p>
      </div>
      <div className="flex items-center gap-1">
        <ActiveBadge isActive={category.is_active} />
        <button
          type="button"
          onClick={() => onEdit(category)}
          className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
          aria-label={t("actions.edit")}
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(category)}
          className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
          aria-label={t("actions.delete")}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function renderMobileFlat(
  categories: readonly Category[],
  depth: number,
  onEdit: (c: Category) => void,
  onDelete: (c: Category) => void,
): React.ReactNode[] {
  const rows: React.ReactNode[] = [];
  for (const cat of categories) {
    rows.push(
      <MobileRow
        key={cat.id}
        category={cat}
        depth={depth}
        onEdit={onEdit}
        onDelete={onDelete}
      />,
    );
    if (cat.children.length > 0) {
      rows.push(...renderMobileFlat(cat.children, depth + 1, onEdit, onDelete));
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CategoryTree({
  categories,
  isLoading,
  onEdit,
  onAddChild,
  onDelete,
  onReorder,
}: CategoryTreeProps) {
  const { t } = useTranslation("admin");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [dragSourceId, setDragSourceId] = useState<string | null>(null);
  const [dragTargetId, setDragTargetId] = useState<string | null>(null);

  // Toggle expand/collapse
  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Drag & drop handlers
  const handleDragStart = useCallback((e: DragEvent, category: Category) => {
    setDragSourceId(category.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", category.id);
  }, []);

  const handleDragOver = useCallback((e: DragEvent, category: Category) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragTargetId(category.id);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent, targetCategory: Category) => {
      e.preventDefault();
      setDragTargetId(null);
      setDragSourceId(null);

      const sourceId = e.dataTransfer.getData("text/plain");
      if (!sourceId || sourceId === targetCategory.id) return;

      // Find siblings at same level as target
      const findSiblings = (
        cats: readonly Category[],
        parentId: string | null,
      ): readonly Category[] | null => {
        for (const cat of cats) {
          if (cat.id === targetCategory.id) {
            // Found target — return its siblings (the array it belongs to)
            return cats;
          }
          if (cat.children.length > 0) {
            const found = findSiblings(cat.children, cat.id);
            if (found) return found;
          }
        }
        return null;
      };

      const siblings = findSiblings(categories, null) ?? categories;

      // Only reorder if both source and target are in the same sibling list
      const sourceIdx = siblings.findIndex((c) => c.id === sourceId);
      const targetIdx = siblings.findIndex((c) => c.id === targetCategory.id);

      if (sourceIdx === -1 || targetIdx === -1) return;

      // Build new order
      const reordered = [...siblings];
      const [moved] = reordered.splice(sourceIdx, 1);
      reordered.splice(targetIdx, 0, moved);

      const items: ReorderItem[] = reordered.map((cat, idx) => ({
        id: cat.id,
        sort_order: idx,
      }));

      onReorder(items);
    },
    [categories, onReorder],
  );

  // Recursive tree renderer
  const renderTree = (
    cats: readonly Category[],
    depth: number,
  ): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    for (const cat of cats) {
      const isExpanded = expanded.has(cat.id);
      nodes.push(
        <TreeRow
          key={cat.id}
          category={cat}
          depth={depth}
          isExpanded={isExpanded}
          onToggle={() => toggleExpand(cat.id)}
          onEdit={onEdit}
          onAddChild={onAddChild}
          onDelete={onDelete}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          isDragTarget={dragTargetId === cat.id && dragSourceId !== cat.id}
        />,
      );
      if (isExpanded && cat.children.length > 0) {
        nodes.push(...renderTree(cat.children, depth + 1));
      }
    }
    return nodes;
  };

  // Loading state
  if (isLoading) {
    return <TreeSkeleton />;
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <FolderOpen className="h-10 w-10 text-stone-300" />
        <p className="mt-3 text-sm font-medium text-stone-600">
          {t("categories.empty")}
        </p>
        <p className="mt-1 text-xs text-stone-400">
          {t("categories.empty_hint")}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop tree view */}
      <div className="hidden sm:block">
        <div className="rounded-lg border border-stone-200 bg-white">
          {renderTree(categories, 0)}
        </div>
      </div>

      {/* Mobile flat list */}
      <div className="sm:hidden">
        <div className="rounded-lg border border-stone-200 bg-white">
          {renderMobileFlat(categories, 0, onEdit, onDelete)}
        </div>
      </div>
    </>
  );
}
